import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Loader2, BadgeCheck, AlertTriangle, Ban } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import { tradeService } from '../services/trade-service';
import type { TariffCodeInfo } from '../types/trade.types';

/**
 * Buscador de partidas arancelarias.
 *
 * **Por qué existe.** El código NANDINA era un campo de texto libre y la
 * alícuota se escribía a mano, en el alta de productos y en la revisión de la
 * proforma. Eso tenía dos consecuencias:
 *
 *  1. El arancel de una importación salía de lo que alguien recordaba, no del
 *     Libro. Un 10 % tecleado y un 10 % oficial son el mismo número con
 *     consecuencias distintas cuando se presenta la DUI.
 *  2. Las 8.109 partidas del catálogo oficial **no llegaban a la orden**: el
 *     Asistente Arancelario y el cálculo de costos vivían separados.
 *
 * Al elegir una partida se guarda su **id**, no solo el código. Eso es lo que
 * permite que la alícuota se resuelva contra el catálogo en cada recálculo — y
 * que si mañana cambia por un decreto, la orden se recalcule sola.
 *
 * Busca por código y por descripción a la vez: quien tiene la proforma delante
 * escribe "8471.30", y quien no, escribe "aire acondicionado".
 */

interface TariffPickerProps {
  /** La partida elegida, si ya hay una. */
  value: TariffCodeInfo | null;
  onChange: (partida: TariffCodeInfo | null) => void;
  /** Texto del código cuando viene de la proforma y todavía no se resolvió. */
  rawCode?: string | null;
  disabled?: boolean;
  /** Compacto para usar dentro de una fila de tabla. */
  dense?: boolean;
}

/** Milisegundos de quietud antes de buscar. */
const DEBOUNCE_MS = 300;

export function TariffPicker({
  value,
  onChange,
  rawCode,
  disabled,
  dense = false,
}: TariffPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TariffCodeInfo[]>([]);
  const [suggestions, setSuggestions] = useState<TariffCodeInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic afuera: sin esto la lista queda flotando sobre el resto
  // del formulario y tapa los campos de abajo.
  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const buscar = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const r = await tradeService.searchTariffCodes(q.trim());
      setResults(r.results ?? []);
      setSuggestions(r.suggestions ?? []);
    } catch {
      setResults([]);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Se espera a que deje de escribir: buscar en cada tecla sobre 8.109 partidas
  // manda una consulta por letra y devuelve resultados de una búsqueda vieja.
  useEffect(() => {
    const t = setTimeout(() => void buscar(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, buscar]);

  // Ya hay una partida elegida: se muestra en vez del buscador.
  if (value) {
    return <PartidaElegida partida={value} onClear={() => onChange(null)} disabled={disabled} />;
  }

  const lista = results.length > 0 ? results : suggestions;
  const esSugerencia = results.length === 0 && suggestions.length > 0;

  return (
    <div ref={contenedor} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Buscá por código o por producto: 8415.10 · aire acondicionado"
          className={cn('pl-8', dense ? 'h-8 text-xs' : 'h-9 text-sm')}
          aria-label="Buscar partida arancelaria"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {/*
        El código que traía la proforma, cuando no se pudo resolver.

        Se muestra para que la persona no tenga que volver al documento: lo ve,
        lo compara y busca. Antes ese texto se guardaba tal cual y quedaba una
        partida que no existe en el arancel.
      */}
      {rawCode && (
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-alerta">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          La proforma decía <strong className="font-mono">{rawCode}</strong> y no se encontró en el
          arancel.
        </p>
      )}

      {open && query.trim().length >= 2 && !loading && (
        <div className="absolute z-50 mt-1 max-h-72 w-full min-w-[420px] overflow-y-auto rounded-lg border bg-popover shadow-lg">
          {lista.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              Ninguna partida con <strong>{query}</strong>. Probá con el nombre del producto en vez
              del código.
            </p>
          ) : (
            <>
              {esSugerencia && (
                <p className="border-b bg-alerta-suave px-3 py-1.5 text-[11px] text-foreground">
                  Ese código no existe. Estas son las partidas más cercanas:
                </p>
              )}
              {lista.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onChange(p);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="flex w-full items-start gap-2.5 border-b px-3 py-2 text-left last:border-0 hover:bg-accent"
                >
                  <span className="shrink-0 font-mono text-xs font-semibold text-prixia-blue">
                    {p.code}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs leading-tight">{p.description}</span>
                    {p.chapterName && (
                      <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                        {p.chapterName}
                      </span>
                    )}
                  </span>
                  <Alicuotas partida={p} />
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Lo que paga la partida, al lado de su nombre.
 *
 * Va en la lista y no solo al elegir: **la alícuota es parte de la decisión**.
 * Entre dos partidas parecidas, una al 0 % y otra al 20 %, elegir a ciegas y
 * enterarse después es exactamente lo que hace que alguien clasifique mal.
 */
function Alicuotas({ partida }: { partida: TariffCodeInfo }) {
  return (
    <span className="flex shrink-0 items-center gap-1 text-[10px]">
      <span
        className={cn(
          'rounded px-1.5 py-0.5 font-mono font-semibold',
          partida.gaRate === 0 ? 'bg-ok-suave text-ok' : 'bg-muted text-muted-foreground'
        )}
        title="Gravamen arancelario"
      >
        GA {partida.gaRate} %
      </span>
      {partida.ivaExempt && (
        <span className="rounded bg-ok-suave px-1.5 py-0.5 text-ok" title="Exenta de IVA">
          sin IVA
        </span>
      )}
      {partida.hasIce && (
        <span className="rounded bg-alerta-suave px-1.5 py-0.5 text-foreground" title="Paga ICE">
          ICE
        </span>
      )}
    </span>
  );
}

/**
 * La partida ya elegida.
 *
 * Muestra el código, la descripción y las alícuotas: quien revisa la orden más
 * tarde tiene que poder ver **por qué** paga lo que paga sin abrir otra pantalla.
 */
function PartidaElegida({
  partida,
  onClear,
  disabled,
}: {
  partida: TariffCodeInfo;
  onClear: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-ok-borde bg-ok-suave px-2.5 py-2 ">
      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-semibold">{partida.code}</span>
          <Alicuotas partida={partida} />
        </p>
        <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
          {partida.description}
        </p>
        {/*
          Una partida prohibida no es "una advertencia más fuerte": la operación
          no existe. Por eso se dice acá y no en un panel de avisos aparte.
        */}
        {partida.hasIehd && (
          <p className="mt-1 flex items-center gap-1 text-[10px] text-alerta">
            <Ban className="h-3 w-3" aria-hidden />
            Paga IEHD — el importe no está incluido en la liquidación.
          </p>
        )}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
          aria-label="Quitar la partida"
          title="Elegir otra partida"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
